import { createFileRoute, useParams } from '@tanstack/react-router';
import { Database, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RunExtractionButton } from '@/components/documents/run-extraction-button';
import { useClassificationsQuery } from '@/lib/api/documents';
import { useDocumentProcessingQuery } from '@/lib/api/document-processing';
import { useEngagementQuery } from '@/lib/api/engagements';
import { useProposalQueueQuery } from '@/lib/api/review-proposals';
import { useSearchIndexHealthQuery } from '@/lib/api/search-index-health';
import { DocumentsLibrary } from '@/components/documents/library';
import { AiQueueBanner } from '@/components/documents/ai-queue-banner';
import { ExtractionStatusBanner } from '@/components/documents/extraction-status-banner';
import { documentsSubtitle } from '@/components/documents/indexed-documents';
import { useUploadModal } from '@/lib/modals/use-upload-modal';

/**
 * T-0099 / C20-137 — Documents library route (prototype parity).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `Screens.documents` (lines 286-342). The header reads
 *   "{n} docs · {sources} sources · last sync {iso}"
 * the right side has the Search index, Run extraction and Upload buttons,
 * and the body is a two-column rail (Sources + Ingestion) over a tabbed
 * document grid. The AI queue banner sits above the body when there's
 * unreviewed proposals or in-flight ingestion.
 *
 * C20-137 fixes vs the prior version (T-0099):
 *   - Search index button now triggers the existing `EditorModalHost`
 *     listener via `data-open-index="${engagementId}"` (was disabled).
 *   - Header subtitle includes the indexed-document count and the engagement-
 *     wide last-indexed timestamp from the T-0120 search-index health
 *     surface (was a client-side updatedAt placeholder). Both counts come
 *     from the documents list since the "15 docs · 15 indexed" fix; see
 *     `indexed-documents.ts`.
 *   - The prototype's "New folder" button is left out (C2-32): there is no
 *     folders endpoint yet (T-0040..T-0044 expose classifications-as-
 *     folders only), and a disabled "Pending" button only advertised that.
 *     Add it back when user-made folders exist.
 *
 * Wires:
 *   - `useDocumentProcessingQuery` → every non-deleted document (all pages):
 *     one grid row each and the total count (T-0160 — the grid used to list
 *     only classified documents, so a file that was never read did not
 *     appear), plus each document's stage with real progress / failure and
 *     Retry (T-0159); the header's "N docs · M indexed" (M = rows whose
 *     status is Indexed) and the extraction banner's wording when the last
 *     run found nothing to read. Classifications enrich classified rows;
 *     their failure is not fatal.
 *   - `useSearchIndexHealthQuery` → engagement-wide last_indexed_at for the
 *     header's "last sync".
 *   - `useClassificationsQuery` → per-doc classification + status chips.
 *   - `useProposalQueueQuery` (decision = pending) → AI queue banner
 *     counts + per-row "N proposals" badges.
 *
 * Routes adjacent to this one:
 *   - `documents/review` — side-by-side AI proposal review.
 *   - `documents/$documentId` — single-doc detail (Excel inline).
 *
 * Modal hosts:
 *   - Upload modal — the "Upload documents" button + drag-drop on the
 *     route root both call `openUploadModal({ engagementId, projectId })`.
 *     The modal drives the T-0040 chunked-upload sequence and polls
 *     parse status until each file lands in the library.
 *   - Search index modal — `data-open-index` triggers
 *     `EditorModalHost.SearchIndexModal` (T-0116 / T-0120).
 */

export const Route = createFileRoute('/engagements/$engagementId/documents/')({
  component: DocumentsLibraryRoute,
});

function DocumentsLibraryRoute() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/documents/' });
  // T-0159: the documents with their pipeline stage and real progress;
  // polled every 3s while any document is still moving.
  const docsList = useDocumentProcessingQuery(engagementId);
  const classifications = useClassificationsQuery(engagementId);
  // The chunked upload endpoint keys on `project_id`, not engagement_id.
  // Resolve that via the engagement DTO returned by the T-0091 endpoint.
  const engagement = useEngagementQuery(engagementId);
  const projectId = engagement.data?.project_id;
  // Pending proposals power both the banner counts and the per-doc badges
  // on cards. We scope to decision=pending so we don't show already-decided
  // proposals in the unreviewed queue.
  const proposals = useProposalQueueQuery(engagementId, { decision: 'pending' });
  // Search-index health drives the header's "last sync" (the prototype's
  // t-meta string). Its document_count is not shown here: the counts come
  // from the documents list the rows render.
  const indexHealth = useSearchIndexHealthQuery(engagementId);

  const openUploadModal = useUploadModal();
  const items = classifications.data?.items ?? [];
  // T-0160 — the documents list is the screen's source of truth: every
  // non-deleted document, classified or not. Classifications only enrich.
  const documents = docsList.data?.documents ?? [];

  // "Pending" for the AI queue spinner = anything not in a terminal status.
  // The classification table uses `confirmed | corrected | failed | predicted | pending`
  // — `confirmed`/`corrected`/`failed` are terminal; the rest are still moving.
  const pendingItems = items.filter(
    (item) =>
      item.status !== 'confirmed' && item.status !== 'corrected' && item.status !== 'failed',
  );

  // Drag-drop affordance — the prototype shows a drop zone "anywhere on
  // the page". Dropping anywhere on the route opens the upload modal so
  // the user can confirm + see per-file progress. The modal owns the
  // actual file ingestion (it picks `dataTransfer.files` from its own
  // drop zone after the user can see what they're about to upload).
  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!projectId) return;
    // Only open if there's at least one file in the drop — empty drops
    // (e.g. a text selection) should fall through silently.
    if ((e.dataTransfer?.files?.length ?? 0) > 0) {
      openUploadModal({ engagementId, projectId });
    }
  }

  function handleUploadClick() {
    if (!projectId) return;
    openUploadModal({ engagementId, projectId });
  }

  // Header subtitle: "{n} docs · {m} indexed · last sync {time}". N and M
  // both count the documents list the rows render, and M uses the rows'
  // own "Indexed" status, so the two agree with the grid, follow its
  // polling, and M can never exceed N. Only "last sync" comes from the
  // search-index health probe.
  const subtitle = documentsSubtitle(docsList.data?.documents, indexHealth.data?.last_indexed_at);

  return (
    <div className="flex flex-col gap-4" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="t-h1 font-semibold text-ink-900">Documents</h2>
          <div className="mt-1 text-meta text-ink-500">{subtitle}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Wired: triggers EditorModalHost's SearchIndexModal (T-0116 / T-0120). */}
          <Button
            variant="outline"
            size="sm"
            type="button"
            data-open-index={engagementId}
            title="Open the search-index health modal"
          >
            <Database className="h-4 w-4" aria-hidden="true" />
            <span>Search index</span>
          </Button>
          {/* No "New folder" button: documents are grouped by type and there
              are no user-made folders yet, so a disabled "Pending" button only
              advertised our backlog (C2-32). */}
          <RunExtractionButton engagementId={engagementId} />
          <Button
            size="sm"
            type="button"
            onClick={handleUploadClick}
            disabled={!projectId}
            title={projectId ? 'Open upload modal' : 'Resolving project context…'}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            <span>Upload documents</span>
          </Button>
        </div>
      </header>

      {/* The latest AI extraction run: reading, waiting on a busy AI service,
          degraded (sections skipped) or failed, in plain words. */}
      <ExtractionStatusBanner engagementId={engagementId} documents={docsList.data?.documents} />

      <AiQueueBanner
        engagementId={engagementId}
        pendingItems={pendingItems}
        proposals={proposals.data?.items ?? []}
      />

      <DocumentsLibrary
        engagementId={engagementId}
        items={items}
        documents={documents}
        proposals={proposals.data?.items ?? []}
        pendingItems={pendingItems}
        // A classifications failure must not hide the documents (T-0160).
        isLoading={docsList.isLoading}
        error={docsList.error}
      />
    </div>
  );
}
